import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Store, Package, Gift, Wallet, X, Phone, MapPin, Loader2, Printer, Send } from "lucide-react";
import { DealerTrueMarginCard } from "@/components/DealerTrueMarginCard";

const formatINR = (paise) => {
  if (!paise) return "₹0";
  const r = paise / 100;
  if (r >= 10000000) return `₹${(r / 10000000).toFixed(2)}Cr`;
  if (r >= 100000) return `₹${(r / 100000).toFixed(2)}L`;
  if (r >= 1000) return `₹${(r / 1000).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
};

/**
 * <DealerCatalogueDialog>
 * Dynamic per-dealer catalogue — pulls `/api/dealers/{id}/catalogue` which returns
 * dealer-specific products (filtered by their region), active schemes that match
 * their party_type + region + window, and a ledger snapshot.
 *
 * The print button opens the browser's print dialog with the dialog body styled
 * for A4 — gives a usable PDF without needing a server-side renderer.
 */
export function DealerCatalogueDialog({ open, onClose, partyId }) {
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);

  useEffect(() => {
    if (!open || !partyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${API}/dealers/${partyId}/catalogue`);
        const j = await res.json();
        if (!cancelled && j.success) setCatalogue(j.data);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, partyId]);

  const handlePrint = () => {
    // Open dialog content in print mode
    window.print();
  };

  /**
   * Build a one-tap WhatsApp message for the dealer.
   * Strategy: save the catalogue rows as a CSV via the existing
   * /api/reports/save-to-storage flow (reusing the same object-storage path
   * we built for Reports-to-S3 in iter81), get a share URL, then build a
   * wa.me/<mobile>?text=<summary+link> deep-link. Opens in a new tab.
   *
   * Falls back to text-only summary if the upload fails (e.g. no internet
   * on the salesman's tablet) — they can still send the message manually.
   */
  const handleSendWhatsApp = async () => {
    if (!catalogue) return;
    const mobile = (catalogue.party.mobile_number || "").replace(/[^0-9]/g, "");
    if (!mobile) {
      toast.error("Dealer mobile number not on file — add it in the Dealer profile first.");
      return;
    }
    setSendingWa(true);

    // Try to upload a dealer-specific CSV snapshot so the link is shareable.
    let shareLine = "";
    try {
      const res = await authFetch(
        `${API}/reports/save-to-storage/dealers?region=${encodeURIComponent(catalogue.party.region_id || "ALL")}`,
        { method: "POST" }
      );
      const j = await res.json();
      if (res.ok && j.success && j.data?.storage_path) {
        const tok = typeof localStorage !== "undefined" ? localStorage.getItem("session_token") : "";
        shareLine = `\n\n📄 Full catalogue: ${API}/files/${j.data.storage_path}?auth=${tok}`;
      }
    } catch { /* fall back to text-only message */ }

    const outstanding = catalogue.ledger?.outstanding_balance || 0;
    const collected = catalogue.ledger?.total_collected || 0;
    const totalOrdered = catalogue.ledger?.total_order_value || 0;
    const inr = (paise) => `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;
    const lines = [
      `🏬 *${catalogue.party.firm_name}* — Avi Cement catalogue snapshot`,
      "",
      `📦 Active products: ${catalogue.products?.length || 0}`,
      `🎁 Active schemes for you: ${catalogue.schemes?.length || 0}`,
      "",
      `💰 Outstanding balance: *${inr(outstanding)}*`,
      `✅ Lifetime collected: ${inr(collected)}`,
      `🧾 Lifetime ordered: ${inr(totalOrdered)}`,
    ];
    if ((catalogue.schemes || []).length > 0) {
      lines.push("");
      lines.push("🎁 *Active schemes:*");
      catalogue.schemes.slice(0, 3).forEach((s) => {
        lines.push(`• ${s.scheme_name}${s.end_date ? ` (till ${s.end_date})` : ""}`);
      });
    }
    const text = lines.join("\n") + shareLine + "\n\nReply to this message for orders or queries. 🙏";

    // wa.me requires international format. Default to +91 if no country code.
    const fullNumber = mobile.length === 10 ? `91${mobile}` : mobile;
    const url = `https://wa.me/${fullNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp opened with pre-filled catalogue message");
    setSendingWa(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full" data-testid="dealer-catalogue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-[#8B1A1A]" />
            Dealer Catalogue
            {catalogue && (
              <Badge variant="outline" className="ml-2 text-[10px]">
                {catalogue.meta?.product_count ?? catalogue.products?.length ?? 0} products
                {(catalogue.meta?.scheme_count ?? catalogue.schemes?.length ?? 0) > 0 &&
                  <> · {catalogue.meta?.scheme_count ?? catalogue.schemes?.length} schemes</>
                }
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !catalogue ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Dealer header */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800/40 dark:to-slate-800/20" data-testid="catalogue-dealer-header">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">{catalogue.party.firm_name}</h2>
                <Badge className="bg-[#8B1A1A] text-white">{catalogue.party.party_type}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                {catalogue.party.contact_person && (
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Contact</p><p className="text-sm font-medium">{catalogue.party.contact_person}</p></div>
                )}
                {catalogue.party.mobile_number && (
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-500"><Phone className="w-2.5 h-2.5 inline mr-0.5" /> Mobile</p><p className="text-sm font-mono">{catalogue.party.mobile_number}</p></div>
                )}
                {catalogue.party.district && (
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-500"><MapPin className="w-2.5 h-2.5 inline mr-0.5" /> District</p><p className="text-sm">{catalogue.party.district} · {catalogue.party.region_id}</p></div>
                )}
                {catalogue.party.gstin && (
                  <div><p className="text-[10px] uppercase tracking-wider text-slate-500">GSTIN</p><p className="text-sm font-mono">{catalogue.party.gstin}</p></div>
                )}
              </div>
            </div>

            {/* Ledger snapshot */}
            <div className="grid grid-cols-3 gap-3" data-testid="catalogue-ledger">
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20">
                <p className="text-[10px] uppercase tracking-wider text-blue-700">Total Orders</p>
                <p className="text-lg font-bold text-blue-900 dark:text-blue-200 font-mono mt-0.5">{catalogue.ledger.total_orders}</p>
                <p className="text-[10px] text-blue-700 mt-0.5">{formatINR(catalogue.ledger.total_order_value)}</p>
              </div>
              <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">Collected</p>
                <p className="text-lg font-bold text-emerald-900 dark:text-emerald-200 font-mono mt-0.5">{formatINR(catalogue.ledger.total_collected)}</p>
              </div>
              <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20">
                <p className="text-[10px] uppercase tracking-wider text-red-700">Outstanding</p>
                <p className="text-lg font-bold text-red-900 dark:text-red-200 font-mono mt-0.5">{formatINR(catalogue.ledger.outstanding_balance)}</p>
              </div>
            </div>

            {/* True Margin Card (P2 iter84) */}
            <DealerTrueMarginCard partyId={catalogue.party.party_id} />

            {/* Products */}
            <div>
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-violet-600" /> Available products
                <span className="text-slate-400 font-normal">({catalogue.products?.length || 0})</span>
              </p>
              {(catalogue.products || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No products available for this dealer's region.</p>
              ) : (
                <div className="border rounded-md overflow-hidden dark:border-slate-700">
                  <table className="w-full text-xs" data-testid="catalogue-products">
                    <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5">Code</th>
                        <th className="text-left px-2 py-1.5">Product</th>
                        <th className="text-left px-2 py-1.5">Brand</th>
                        <th className="text-right px-2 py-1.5">MRP</th>
                        <th className="text-right px-2 py-1.5">Dealer Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogue.products.map((p) => (
                        <tr key={p.product_id} className="border-t dark:border-slate-700" data-testid={`catalogue-product-${p.product_id}`}>
                          <td className="px-2 py-1.5 font-mono">{p.product_code || p.product_id}</td>
                          <td className="px-2 py-1.5 font-medium">{p.product_name}</td>
                          <td className="px-2 py-1.5">{p.brand || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{p.mrp ? formatINR(p.mrp) : "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">{p.dealer_price ? formatINR(p.dealer_price) : p.mrp ? formatINR(p.mrp) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Schemes */}
            {(catalogue.schemes || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 text-amber-600" /> Active schemes for this dealer
                  <span className="text-slate-400 font-normal">({catalogue.schemes.length})</span>
                </p>
                <div className="space-y-2" data-testid="catalogue-schemes">
                  {catalogue.schemes.map((s) => (
                    <div key={s.scheme_id} className="p-3 rounded-lg border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10" data-testid={`catalogue-scheme-${s.scheme_id}`}>
                      <div className="flex items-baseline justify-between flex-wrap gap-2">
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{s.scheme_name}</p>
                        <Badge variant="outline" className="text-[10px]">
                          {s.start_date} → {s.end_date || "open"}
                        </Badge>
                      </div>
                      {s.description && <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{s.description}</p>}
                      <div className="flex gap-3 mt-1.5 text-[11px] text-slate-500">
                        {s.scheme_type && <span><strong>Type:</strong> {s.scheme_type}</span>}
                        {s.discount_pct && <span><strong>Discount:</strong> {s.discount_pct}%</span>}
                        {s.bonus_amount && <span><strong>Bonus:</strong> {formatINR(s.bonus_amount)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-slate-400 text-right">
              Generated {new Date(catalogue.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </p>
          </div>
        )}

        <DialogFooter className="print:hidden">
          <Button
            onClick={handleSendWhatsApp}
            disabled={!catalogue || sendingWa}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="catalogue-whatsapp-btn"
          >
            {sendingWa ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send via WhatsApp
          </Button>
          <Button variant="outline" onClick={handlePrint} disabled={!catalogue} data-testid="catalogue-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
          </Button>
          <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DealerCatalogueDialog;
